type Severity = "high" | "med" | "low";

interface Issue {
  severity: Severity;
  title: string;
  desc: string;
  source: string;
}

interface DossierCard {
  ref: string;
  title: string;
  meta: string;
  variant: "warning" | "danger";
  badge: string;
  issues: Issue[];
}

const SEV_LABEL: Record<Severity, string> = {
  high: "Hoog",
  med: "Midden",
  low: "Laag",
};

const CARDS: DossierCard[] = [
  {
    ref: "EA-2026-042 · 23 jun 2026",
    title: "Survey over migratie en gezondheid",
    meta: "Karim El Idrissi · Faculteit Bedrijfswetenschappen en Economie · AI-volledigheid: 61%",
    variant: "warning",
    badge: "3 issues gevonden",
    issues: [
      {
        severity: "high",
        title: "GDPR / dataverwerkingsplan ontbreekt",
        desc: "Sectie 4.2 vermeldt gegevensopslag maar specificeert geen bewaartermijn of verwerkingsgrondslag.",
        source: "Bron: formulier p.4 §4.2",
      },
      {
        severity: "high",
        title: "Toestemmingsformulier niet bijgevoegd",
        desc: "Informed consent voor kwetsbare populatie (migranten) is vereist maar ontbreekt als bijlage.",
        source: "Vereiste bijlage: Bijlage B",
      },
      {
        severity: "med",
        title: "Steekproefgrootte onduidelijk",
        desc: 'Methodologische sectie vermeldt "voldoende deelnemers" zonder concrete schatting of onderbouwing.',
        source: "Bron: formulier p.6 §5.1",
      },
    ],
  },
  {
    ref: "EA-2026-041 · 22 jun 2026",
    title: "Interviews over psychische kwetsbaarheid",
    meta: "Sofie Peeters · Faculteit Geneeskunde en Gezondheidswetenschappen · AI-volledigheid: 45%",
    variant: "danger",
    badge: "5 issues gevonden",
    issues: [
      {
        severity: "high",
        title: "Kwetsbare doelgroep: verhoogde zorgplicht niet verantwoord",
        desc: "Studie betrekt personen met psychische aandoeningen. Aanvullende waarborgen en inclusiecriteria ontbreken volledig.",
        source: "Bron: formulier p.3 §3.1",
      },
      {
        severity: "high",
        title: "Toestemmingsformulier ontbreekt",
        desc: "Geen informed consent bijgevoegd voor psychisch kwetsbare deelnemers.",
        source: "Vereiste bijlage: Bijlage B",
      },
      {
        severity: "high",
        title: "GDPR-sectie incompleet",
        desc: "Verwerkingsgrondslag, bewaartermijn en verwijderingsprotocol ontbreken.",
        source: "Bron: formulier p.5 §4.3",
      },
      {
        severity: "med",
        title: "Wervingsprocedure niet omschreven",
        desc: "Hoe worden deelnemers gerekruteerd en via welk kanaal? Ontbreekt in methodologie.",
        source: "Bron: formulier p.6 §5.2",
      },
      {
        severity: "low",
        title: "Verouderd template gebruikt",
        desc: "Formulier versie 2023-v1 ingediend; huidige versie is 2025-v2.",
        source: "Bestand: aanvraagformulier.docx",
      },
    ],
  },
  {
    ref: "EA-2026-037 · 17 jun 2026",
    title: "Experiment over cognitieve belasting",
    meta: "Nathalie Goris · Faculteit Letteren en Wijsbegeerte · AI-volledigheid: 70%",
    variant: "warning",
    badge: "2 issues gevonden",
    issues: [
      {
        severity: "med",
        title: "Interne inconsistentie in de methode",
        desc: "Formulier beschrijft online survey; bijgevoegd protocol verwijst naar laboratoriumexperimenten.",
        source: "Bron: formulier p.5 vs. Protocol_v2.pdf",
      },
      {
        severity: "low",
        title: "Debrief-procedure niet beschreven",
        desc: "Bij cognitief belastend experiment is debriefing vereist; procedure ontbreekt.",
        source: "Bron: formulier p.7 §6.1",
      },
    ],
  },
  {
    ref: "EA-2026-035 · 14 jun 2026",
    title: "Survey over religie en integratie",
    meta: "Yasmine Khalil · Faculteit Bedrijfswetenschappen en Economie · AI-volledigheid: 55%",
    variant: "warning",
    badge: "2 issues gevonden",
    issues: [
      {
        severity: "high",
        title: "Gevoelige data: religieuze overtuiging",
        desc: "Bevraging bevat vragen over religieuze identiteit (bijzondere categorie, art. 9 GDPR). Aanvullende verwerkingsgrondslag vereist maar afwezig.",
        source: "Bron: vragenlijst bijlage p.2",
      },
      {
        severity: "med",
        title: "Anonimisatieprotocol ontbreekt",
        desc: "Hoe worden antwoorden geanonimiseerd na dataverzameling? Niet beschreven.",
        source: "Bron: formulier p.5 §4.4",
      },
    ],
  },
];

export function PrescreeningPage() {
  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Pre-screening</h1>
          <p>
            8 dossiers wachten op actie van de onderzoeker · AI-bevindingen zijn adviserend, niet
            blokkerend
          </p>
        </div>
      </div>

      <div className="info-banner">
        <span className="info-banner__icon">ℹ</span>
        <span>
          AI-pre-screening is adviserend. Onderzoekers kunnen altijd doorgaan of menselijke review
          aanvragen. Secretariaat kan altijd escaleren.
        </span>
      </div>

      <div className="ua-col-sm ua-mt-space">
        {CARDS.map((card) => (
          <div key={card.ref} className={`dossier-card dossier-card--${card.variant}`}>
            <div className="dossier-card__head">
              <div>
                <div className="dossier-card__ref">{card.ref}</div>
                <div className="dossier-card__title">{card.title}</div>
                <div className="dossier-card__meta">{card.meta}</div>
              </div>
              <span className={`badge badge--${card.variant}`}>{card.badge}</span>
            </div>
            <div className="dossier-card__issues">
              {card.issues.map((issue, idx) => (
                <div key={idx} className={`issue-row issue-row--${issue.severity}`}>
                  <span className={`issue-sev issue-sev--${issue.severity}`}>
                    {SEV_LABEL[issue.severity]}
                  </span>
                  <div>
                    <div className="issue-title">{issue.title}</div>
                    <div className="issue-desc">
                      {issue.desc} <span className="issue-source">{issue.source}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="dossier-card__actions">
              <button className="btn btn--ghost px-[14px] py-[6px] text-[13px]">Herzien</button>
              <button className="btn btn--ghost px-[14px] py-[6px] text-[13px]">
                Doorgaan met uitleg
              </button>
              <button className="btn btn--secondary px-[14px] py-[6px] text-[13px]">
                Menselijke review aanvragen
              </button>
            </div>
          </div>
        ))}

        <div className="ua-card-padding">
          + 4 overige dossiers in pre-screening · <a href="#">Toon alles</a>
        </div>
      </div>
    </section>
  );
}
